import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import styled, { keyframes } from "styled-components";
import sanityClient from "../sanityClient";
import habitatLogo from "../assets/images/logos/Buffalo_Hz_2clr.webp";
import {
  FaDonate,
  FaHandsHelping,
  FaHome,
  FaUsers,
  FaClipboardList,
  FaWrench,
  FaInstagram,
  FaFacebook,
  FaTwitter,
  FaLinkedin,
} from "react-icons/fa";
import { Link } from "react-router-dom";

const Connect = () => {
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);

  useEffect(() => {
    // Fetch upcoming events
    sanityClient
      .fetch(
        `*[_type == "event" && startDate > now()] | order(startDate asc) {
          title,
          startDate,
          location,
          ticketLink
        }`
      )
      .then((data) => setEvents(data))
      .catch(console.error);

    // Fetch recent media coverage
    sanityClient
      .fetch(
        `*[_type == "mediaCoverage"] | order(date desc)[0...3] {
          title,
          link,
          date
        }`
      )
      .then((data) => setNews(data))
      .catch(console.error);
  }, []);

  const trackEvent = (action, label) => {
    window.gtag("event", action, {
      event_category: "Connect Page",
      event_label: label,
    });
  };

  return (
    <>
      <Helmet>
        <title>Connect - Habitat for Humanity Buffalo</title>
        <meta
          name="description"
          content="Connect with Habitat for Humanity Buffalo for upcoming events, news, and ways to get involved."
        />
      </Helmet>
      <Container>
        <Logo src={habitatLogo} alt="Habitat for Humanity Buffalo Logo" />
        <Subheading>Join Us in Empowering Buffalo!</Subheading>
        <InfoText>
          Visit the <Link to="/">Habitat for Humanity website</Link> for more
          information, and below are easy links to what’s happening with Habitat
          Buffalo.
        </InfoText>

        {events.length > 0 && (
          <Section>
            <h2>Upcoming Events</h2>
            {events.map((event) => (
              <Button
                key={event.title}
                as={Link}
                to={event.ticketLink || "/events"}
                onClick={() =>
                  trackEvent("click", `Upcoming Event: ${event.title}`)
                }
                aria-label={`Details for ${event.title} on ${new Date(
                  event.startDate
                ).toLocaleDateString()}`}
              >
                <FaClipboardList size={28} />
                <ButtonText>
                  {event.title} -{" "}
                  {new Date(event.startDate).toLocaleDateString()}
                </ButtonText>
              </Button>
            ))}
          </Section>
        )}

        <Section>
          <h2>Habitat Buffalo Turns 40</h2>
          <Button
            as={Link}
            to="https://habitatbuffalo.org/anniversary"
            onClick={() => trackEvent("click", "40th Anniversary Page")}
            aria-label="Visit the Habitat Buffalo 40th Anniversary Page"
          >
            <FaClipboardList size={28} />
            <ButtonText>40th Anniversary Events</ButtonText>
          </Button>
          <Button
            as={Link}
            to="https://habitatbuffalo.betterworld.org/campaigns/400in40"
            onClick={() => trackEvent("click", "400in40 Fundraising Page")}
            aria-label="Donate and get commemorative swag for the 40th Anniversary"
          >
            <FaDonate size={28} />
            <ButtonText>#400in40 Fundraising Page</ButtonText>
          </Button>
        </Section>

        <Section>
          <h2>Habitat Buffalo in the News</h2>
          {news.map((item) => (
            <Button
              key={item.title}
              as={Link}
              to={item.link}
              onClick={() => trackEvent("click", `News Article: ${item.title}`)}
              aria-label={`Read article: ${item.title}`}
            >
              <FaClipboardList size={28} />
              <ButtonText>{item.title}</ButtonText>
            </Button>
          ))}
        </Section>
        <Section>
          <h2>Habitat ReStores</h2>
          <Button
            as={Link}
            to="https://habitatbuffalo.org/restore/locations-and-hours"
            onClick={() => trackEvent("click", "ReStore Locations and Hours")}
            aria-label="ReStore Locations and Hours"
          >
            <FaDonate size={28} />
            <ButtonText>ReStore Locations and Hours</ButtonText>
          </Button>
          <Button
            as={Link}
            to="https://habitatbuffalo.org/restore/donate-items"
            onClick={() => trackEvent("click", "Donate Items to the ReStore")}
            aria-label="Donate Items to the ReStore"
          >
            <FaHandsHelping size={28} />
            <ButtonText>Donate Items to the ReStore</ButtonText>
          </Button>
          <Button
            as={Link}
            to="https://www.ebay.com/str/habitatforhumanitybuffalo"
            onClick={() => trackEvent("click", "Shop the ReStore eBay Store")}
            aria-label="Shop the ReStore eBay Store"
          >
            <FaUsers size={28} />
            <ButtonText>Shop the ReStore eBay Store</ButtonText>
          </Button>
        </Section>

        <Section>
          <h2>Volunteer | Donate | Give Back</h2>
          <Button
            as={Link}
            to="https://habitatbuffalo.org/donate"
            onClick={() => trackEvent("click", "Make a Donation")}
            aria-label="Make a donation to Habitat Buffalo"
          >
            <FaDonate size={28} />
            <ButtonText>Make a Donation</ButtonText>
          </Button>
          <Button
            as={Link}
            to="https://habitatbuffalo.org/restore/donate-items"
            onClick={() => trackEvent("click", "Donate Goods")}
            aria-label="Donate goods to Habitat Buffalo"
          >
            <FaHandsHelping size={28} />
            <ButtonText>Donate Goods</ButtonText>
          </Button>
          <Button
            as={Link}
            to="https://habitatbuffalo.org/volunteer"
            onClick={() => trackEvent("click", "Get Involved")}
            aria-label="Get involved with Habitat Buffalo"
          >
            <FaUsers size={28} />
            <ButtonText>Get Involved</ButtonText>
          </Button>
          <Button
            as={Link}
            to="https://habitatbuffalo.org/get-involved/habitat-young-professionals"
            onClick={() => trackEvent("click", "Join HYP")}
            aria-label="Join Habitat Young Professionals"
          >
            <FaHandsHelping size={28} />
            <ButtonText>Join the Habitat Young Professionals (HYP)</ButtonText>
          </Button>
        </Section>

        <Section>
          <h2>Our Programs and Resources</h2>
          <Button
            as={Link}
            to="https://habitatbuffalo.org/homeownership"
            onClick={() => trackEvent("click", "Become a Homeowner")}
            aria-label="Learn how to become a homeowner"
          >
            <FaHome size={28} />
            <ButtonText>Become a Homeowner</ButtonText>
          </Button>
          <Button
            as={Link}
            to="https://habitatbuffalo.org/repairs"
            onClick={() => trackEvent("click", "Request Critical Home Repairs")}
            aria-label="Request critical home repairs"
          >
            <FaWrench size={28} />
            <ButtonText>Request Critical Home Repairs</ButtonText>
          </Button>
          <Button
            as={Link}
            to="/who-we-are/career-opportunities"
            onClick={() => trackEvent("click", "Job Opportunities")}
            aria-label="View job opportunities"
          >
            <FaClipboardList size={28} />
            <ButtonText>Job Opportunities</ButtonText>
          </Button>
        </Section>

        <Section>
          <h2>Follow Us</h2>
          <SocialMediaIcons>
            <SocialIcon
              as="a"
              href="https://www.instagram.com/habitatbuffalo_/"
              onClick={() => trackEvent("click", "Instagram")}
              aria-label="Follow us on Instagram"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaInstagram size={28} />
            </SocialIcon>
            <SocialIcon
              as="a"
              href="https://www.facebook.com/HabitatBuffalo/"
              onClick={() => trackEvent("click", "Facebook")}
              aria-label="Follow us on Facebook"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaFacebook size={28} />
            </SocialIcon>
            <SocialIcon
              as="a"
              href="https://twitter.com/HabitatBuffalo"
              onClick={() => trackEvent("click", "Twitter")}
              aria-label="Follow us on Twitter"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaTwitter size={28} />
            </SocialIcon>
            <SocialIcon
              as="a"
              href="https://www.linkedin.com/company/458301"
              onClick={() => trackEvent("click", "LinkedIn")}
              aria-label="Follow us on LinkedIn"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaLinkedin size={28} />
            </SocialIcon>
          </SocialMediaIcons>
        </Section>

        <NewsletterSignup />
      </Container>
    </>
  );
};

const NewsletterSignup = () => {
  const [status, setStatus] = useState(null);

  return (
    <SignupContainer>
      {status === "success" ? (
        <ThankYouMessage>
          <h2>Thank You for Signing Up!</h2>
          <p>
            You have successfully signed up for the Habitat Buffalo newsletter.
            Keep an eye on your inbox for the latest stories, updates, and ways
            you can support our local families. We appreciate your support!
          </p>
        </ThankYouMessage>
      ) : (
        <>
          <Title>Sign-up for Our Newsletter</Title>
          <Description>
            Stay up to date with Habitat Buffalo stories and ways you can
            support our local families. Our quarterly newsletter includes
            updates on what we've been doing, upcoming events, a list of current
            programs, and volunteer opportunities. Sign up to get news and event
            information sent directly to your inbox!
          </Description>
          <Form
            action="https://habitatbuffalo.us15.list-manage.com/subscribe/post?u=5d7743596ea119fff30b64404&amp;id=680c77a44a"
            method="POST"
            target="_blank"
          >
            <Input
              id="first-name"
              type="text"
              name="FNAME"
              placeholder="First Name"
              required
            />
            <Input
              id="last-name"
              type="text"
              name="LNAME"
              placeholder="Last Name"
              required
            />
            <Input
              id="email"
              type="email"
              name="EMAIL"
              placeholder="Email"
              required
            />
            <SignUpButton type="submit">Sign Up</SignUpButton>
          </Form>
        </>
      )}
    </SignupContainer>
  );
};

// Styled Components
const Container = styled.div`
  width: 90%;
  max-width: 1200px;
  margin: 20px auto;
  padding: 20px;
  background-color: white;
`;

const Logo = styled.img`
  display: block;
  margin: 0 auto 20px;
  width: 80%;
  max-width: 250px;
`;

const Subheading = styled.h1`
  text-align: center;
  margin-bottom: 40px;
  color: var(--hhb-darkblue);
`;

const InfoText = styled.p`
  text-align: center;
  margin-bottom: 40px;
  font-size: 18px;
`;

const Section = styled.section`
  margin-bottom: 40px;

  h2 {
    margin-bottom: 10px;
    color: var(--hhb-darkblue);
  }
`;

const Button = styled(Link)`
  display: flex;
  align-items: center;
  background-color: var(--hhb-primary);
  color: white;
  padding: 10px;
  margin: 10px 0;
  text-decoration: none;
  border-radius: 5px;
  font-size: 16px;
  justify-content: flex-start;
  transition: background-color 0.3s;
  cursor: pointer;

  svg {
    margin-right: 10px;
  }

  &:hover {
    background-color: #00a0c0;
  }
`;

const ButtonText = styled.span`
  flex: 1;
  text-align: center;
`;

const SocialMediaIcons = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
`;

const SocialIcon = styled.a`
  color: var(--hhb-primary);
  transition: color 0.3s;

  &:hover {
    color: #333;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const SignupContainer = styled.section`
  background-color: var(--hhb-green);
  color: var(--hhb-white);
  padding: 40px 20px;
  text-align: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  font-size: var(--font-size-heading);
  margin-bottom: 20px;
`;

const Description = styled.p`
  font-size: var(--font-size-body);
  margin-bottom: 30px;
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto;
  text-align: left;
`;

const Form = styled.form`
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  gap: 10px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const Input = styled.input`
  padding: 10px;
  font-size: var(--font-size-body);
  border: 1px solid #ccc;
  border-radius: 5px;
  width: 200px;

  @media (max-width: 768px) {
    width: calc(100% - 22px);
    padding-left: 10px;
    padding-right: 10px;
  }
`;

const SignUpButton = styled.button`
  padding: 10px 20px;
  background-color: var(--hhb-orange);
  color: var(--hhb-white);
  text-transform: uppercase;
  font-weight: bold;
  border: none;
  border-radius: 5px;
  cursor: pointer;

  &:hover {
    background-color: #e65a1b;
  }
`;

const ThankYouMessage = styled.div`
  font-size: var(--font-size-body);
  color: var(--hhb-white);
  margin-top: 30px;
  animation: ${fadeIn} 1s ease-in;

  h2 {
    font-size: var(--font-size-heading);
    margin-bottom: 20px;
  }

  p {
    font-size: var(--font-size-body);
    margin: 0 auto;
    max-width: 800px;
  }
`;

export default Connect;
